///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// 3D sea-state marker for one station, built from solids in a local frame
/// with +X east, +Y up and +Z north at the station, so every piece sits in
/// the depth buffer like the ember dome does (no billboard z-fighting, no
/// clipping against draped lines):
///
///   sea-state buoy  ember dome sized and colored by wave height · a low
///                   wedge on the surface pointing into the swell · a torus
///                   whose thickness is the dominant period, breathing at
///                   that period · a square shaft into the wind, length by
///                   speed, with a cross-bar from gale force
///   coastal station cone colored by wind, with the wind shaft
///   tide gauge      cone in the tide color
///   silent station  thin grey torus
///   DART            octahedron, translucent until it reports, solid after
///   limited         small plain dome
///
/// The dome keeps a small screen-space floor so stations stay a fleck from
/// orbit; the decorations scale up modestly to stay legible at regional
/// altitude and drop out beyond that (see setViewScale).
///

import { EmberDomeNode } from "@/earthbrowser/entities/EmberDome/EmberDomeNode"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet, Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type color, rgba } from "@stovokor/math/color"
import { trig, vec3 } from "@stovokor/math"
import { matrix } from "@stovokor/math/matrix"
import { type Geometry } from "@stovokor/shader/Geometry"
import { type Material } from "@stovokor/shader/Material"
import { cullFace } from "@stovokor/shader/ShaderActions"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { sharedBuoyMarkerMeshes } from "./BuoyMarkerGeometry"

export enum BuoyMarkerShape {
	SeaState = 0,
	Coastal = 1,
	Silent = 2,
	Dart = 3,
	Limited = 4,
}

export interface BuoyMarkerStyle {
	shape: BuoyMarkerShape
	color: color
	/** km, dome / symbol radius at scale 1 */
	radiusKm: number
	/** degrees from north, direction waves come from */
	waveDirectionDeg: number | null
	/** seconds, dominant wave period */
	periodSec: number | null
	/** degrees from north, direction wind comes from */
	windDirectionDeg: number | null
	/** m/s */
	windSpeedMps: number | null
	/** 0..1 dome pulse amplitude */
	pulse: number
	/** Fill the DART octahedron: the station is reporting water-column data */
	solid?: boolean
}

// Dome appearance (see EmberDomeNode): solid enough to read over dark
// water; the far-mode floor keeps a faint fleck visible from orbit
const cDomeFill = 0.6
const cDomeFarFill = 0.35
const cDomeMinAngular = 0.0009

// Period ring: radius relative to the dome, thickness class by period
const cRingRadiusFactor = 1.45
const cRingBreath = 0.06
// Wind shaft: starts outside the ring, grows with speed, square section
const cShaftStartFactor = 1.75
const cShaftMinLength = 0.6
const cShaftLengthPerMps = 0.11
const cShaftMaxMps = 32
const cShaftThickness = 0.16
const cGaleMps = 17.2
const cShaftColor = rgba(0.95, 0.97, 1.0, 0.95)
const cDegToRad = Math.PI / 180

export class BuoyMarkerNode extends NodeBase {
	private readonly dome: EmberDomeNode
	private readonly decorations = new NodeBase()
	private readonly wedge: SolidNode
	private readonly rings: [SolidNode, SolidNode, SolidNode]
	private readonly shaft: SolidNode
	private readonly crossbar: SolidNode
	private readonly cone: SolidNode
	private readonly octahedron: SolidNode
	private readonly silentRing: SolidNode

	private style?: BuoyMarkerStyle
	private ringIndex = -1
	private ringBase = matrix.identity
	private viewScale = 1
	private frame = matrix.identity
	private currentOpacity = 1
	private phase = 0

	constructor(context: StovokorContext, phase: number = 0) {
		super()
		this.phase = phase
		const meshes = sharedBuoyMarkerMeshes(context)

		this.dome = new EmberDomeNode(context)
		this.dome.fillAlpha = cDomeFill
		this.dome.farFillAlpha = cDomeFarFill
		this.dome.minAngularSize = cDomeMinAngular
		this.addNode(this.dome)

		this.wedge = new SolidNode(context, meshes.wedge)
		this.rings = [
			new SolidNode(context, meshes.rings[0]),
			new SolidNode(context, meshes.rings[1]),
			new SolidNode(context, meshes.rings[2]),
		]
		this.shaft = new SolidNode(context, meshes.shaft)
		this.crossbar = new SolidNode(context, meshes.shaft)
		this.cone = new SolidNode(context, meshes.cone)
		this.octahedron = new SolidNode(context, meshes.octahedron)
		this.silentRing = new SolidNode(context, meshes.rings[0])
		for (const node of [this.wedge, ...this.rings, this.shaft, this.crossbar, this.cone, this.octahedron, this.silentRing]) {
			node.visible = false
			this.decorations.addNode(node)
		}
		this.addNode(this.decorations)
	}

	/** East, up and north unit vectors in the parent's frame */
	public setFrame(east: vec3, up: vec3, north: vec3): void {
		this.frame = new matrix([
			east.x, east.y, east.z, 0,
			up.x, up.y, up.z, 0,
			north.x, north.y, north.z, 0,
			0, 0, 0, 1,
		])
		this.applyTransform()
	}

	public setStyle(style: BuoyMarkerStyle): void {
		this.style = style
		const r = style.radiusKm
		const all = [this.wedge, ...this.rings, this.shaft, this.crossbar, this.cone, this.octahedron, this.silentRing]
		for (const node of all) node.visible = false

		this.dome.color = style.color
		this.dome.pulse = style.pulse
		this.dome.visible = style.shape === BuoyMarkerShape.SeaState || style.shape === BuoyMarkerShape.Limited
		this.dome.transform = matrix.scale(r)
		this.ringIndex = -1

		switch (style.shape) {
			case BuoyMarkerShape.SeaState: {
				if (style.waveDirectionDeg != null) {
					this.wedge.visible = true
					this.wedge.color = shade(style.color, 0.9)
					this.wedge.transform = matrix.scale(r).multiply(bearing(style.waveDirectionDeg))
				}
				if (style.periodSec != null && style.periodSec > 0) {
					this.ringIndex = style.periodSec < 8 ? 0 : style.periodSec < 12 ? 1 : 2
					const ring = this.rings[this.ringIndex]
					ring.visible = true
					ring.color = shade(style.color, 1.1, 0.75)
					this.ringBase = matrix.scale(r * cRingRadiusFactor)
					ring.transform = this.ringBase
				}
				this.placeShaft(r, style)
				break
			}
			case BuoyMarkerShape.Coastal: {
				this.cone.visible = true
				this.cone.color = style.color
				this.cone.transform = matrix.scale(r)
				this.placeShaft(r, style)
				break
			}
			case BuoyMarkerShape.Silent: {
				this.silentRing.visible = true
				this.silentRing.color = style.color
				this.silentRing.transform = matrix.scale(r)
				break
			}
			case BuoyMarkerShape.Dart: {
				this.octahedron.visible = true
				this.octahedron.color = style.solid ? style.color : withAlpha(style.color, style.color.a * 0.45)
				// Rest a point on the surface
				this.octahedron.transform = matrix.scale(r).multiply(matrix.translate(0, r, 0))
				break
			}
			case BuoyMarkerShape.Limited:
				break
		}
	}

	/** Wind shaft from the symbol edge toward where the wind comes from */
	private placeShaft(r: number, style: BuoyMarkerStyle): void {
		if (style.windDirectionDeg == null || style.windSpeedMps == null || style.windSpeedMps <= 0.5) return
		const speed = Math.min(cShaftMaxMps, style.windSpeedMps)
		const start = r * (this.ringIndex >= 0 ? cShaftStartFactor : 1.2)
		const length = r * (cShaftMinLength + speed * cShaftLengthPerMps)
		const thickness = r * cShaftThickness
		const rotation = bearing(style.windDirectionDeg)

		this.shaft.visible = true
		this.shaft.color = cShaftColor
		this.shaft.transform = matrix.scale(thickness, thickness, length)
			.multiply(matrix.translate(0, 0, start))
			.multiply(rotation)

		if (style.windSpeedMps >= cGaleMps) {
			this.crossbar.visible = true
			this.crossbar.color = cShaftColor
			// A short bar across the tip of the shaft
			this.crossbar.transform = matrix.scale(r * 0.7, thickness, thickness)
				.multiply(matrix.translate(0, 0, start + length - thickness))
				.multiply(rotation)
		}
	}

	public set opacity(value: number) {
		this.currentOpacity = value
		this.dome.opacity = value
		for (const node of [this.wedge, ...this.rings, this.shaft, this.crossbar, this.cone, this.octahedron, this.silentRing]) {
			node.opacity = value
		}
	}

	/**
	 * Per-frame scale: `scale` enlarges the whole marker so it stays
	 * legible as the camera rises; `showDecorations` false leaves only the
	 * dome (with its own far-mode floor) for orbit views
	 */
	public setViewScale(scale: number, showDecorations: boolean): void {
		if (scale !== this.viewScale) {
			this.viewScale = scale
			this.applyTransform()
		}
		this.decorations.visible = showDecorations
	}

	/** Outer extent of the symbol in km at the current view scale */
	public get extentKm(): number {
		const r = this.style?.radiusKm ?? 1
		return r * 2.2 * this.viewScale
	}

	private applyTransform(): void {
		this.transform = matrix.scale(this.viewScale).multiply(this.frame)
	}

	public override render(state: RenderState): RenderActionSet[] {
		if (this.currentOpacity <= 0.002) return []
		// Breathe the period ring at the station's real wave period
		if (this.decorations.visible && this.ringIndex >= 0 && this.style?.periodSec) {
			const seconds = (state.realTime.julian % 1) * 86400 + this.phase
			const breath = 1 + cRingBreath * Math.sin(seconds * trig.two_pi / Math.max(3, this.style.periodSec))
			this.rings[this.ringIndex].transform = this.ringBase.multiply(matrix.scale(breath))
		}
		return super.render(state)
	}
}

/** Rotation about up taking +Z (north) to a compass bearing (degrees clockwise from north) */
function bearing(deg: number): matrix {
	return matrix.axisRotation(vec3.unitY, deg * cDegToRad)
}

function shade(c: color, factor: number, alpha: number = c.a): color {
	return rgba(Math.min(1, c.r * factor), Math.min(1, c.g * factor), Math.min(1, c.b * factor), alpha)
}

function withAlpha(c: color, alpha: number): color {
	return rgba(c.r, c.g, c.b, alpha)
}

/** One flat-shaded solid piece of the marker (buoy_marker.glsl) */
class SolidNode extends NodeBase {
	private readonly solidRenderer: SolidRenderer

	constructor(context: StovokorContext, geometry: Geometry) {
		super()
		this.solidRenderer = new SolidRenderer(context, geometry)
		this.renderer = this.solidRenderer
	}

	public set color(value: color) {
		this.solidRenderer.color.value = value
	}

	public set opacity(value: number) {
		this.solidRenderer.opacity.value = value
	}
}

class SolidRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public modelView = new UniformDynamic<matrix>()
	public color = new UniformDynamic<color>(rgba(1, 1, 1, 1))
	public opacity = new UniformDynamic<number>(1)

	constructor(context: StovokorContext, geometry: Geometry) {
		// Transparent material so the pieces draw in the blended pass with the dome
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("buoy_marker"), geometry, [transparentMaterial])
		// Pieces are thin; draw both faces so a slightly inside-out facet never vanishes
		this.preActions = [cullFace(context, false)]
		this.postActions = [cullFace(context, true)]
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
		this.modelView.value = state.modelView
	}
}
